import React, { useState, useEffect } from 'react';
import {
  ShoppingBagIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  UsersIcon,
  ShoppingCartIcon,
  ArchiveBoxXMarkIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';

interface DashboardStats {
  total_users: number;
  total_customers: number;
  total_orders: number;
  pending_orders: number;
  total_products: number;
  out_of_stock_products: number;
  pending_payments: number;
  total_revenue: number;
  monthly_revenue: number;
}

interface DashboardData {
  stats: DashboardStats;
  recent_orders: any[];
  top_products: any[];
}

interface RecentOrder {
  id: number;
  order_number: string;
  user: {
    name: string;
    email: string;
  };
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
}

interface TopProduct {
  id: number;
  name: string;
  total_sold: number;
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get('/admin/dashboard');
      setDashboardData(data);
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + ' MMK';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatsCards = () => {
    if (!dashboardData) return [];
    
    const { stats } = dashboardData;
    return [
      {
        name: 'Total Revenue',
        value: formatCurrency(stats.total_revenue),
        subValue: `${formatCurrency(stats.monthly_revenue)} this month`,
        icon: BanknotesIcon,
        color: 'from-green-400 to-green-600',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50'
      },
      {
        name: 'Total Orders',
        value: stats.total_orders.toLocaleString(),
        subValue: `${stats.pending_orders} pending`,
        icon: ShoppingCartIcon,
        color: 'from-blue-400 to-blue-600',
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-50'
      },
      {
        name: 'Total Customers',
        value: stats.total_customers.toLocaleString(),
        subValue: `${stats.total_users} total users`,
        icon: UsersIcon,
        color: 'from-purple-400 to-purple-600',
        textColor: 'text-purple-600',
        bgColor: 'bg-purple-50'
      },
      {
        name: 'Products',
        value: stats.total_products.toLocaleString(),
        subValue: `${stats.out_of_stock_products} out of stock`,
        icon: ShoppingBagIcon,
        color: 'from-orange-400 to-orange-600',
        textColor: 'text-orange-600',
        bgColor: 'bg-orange-50'
      }
    ];
  };

  const getAlertCards = () => {
    if (!dashboardData) return [];
    
    const { stats } = dashboardData;
    const alerts = [];
    
    if (stats.pending_payments > 0) {
      alerts.push({
        title: 'Pending Payments',
        count: stats.pending_payments,
        description: 'Manual payments awaiting verification',
        icon: ClockIcon,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      });
    }
    
    if (stats.out_of_stock_products > 0) {
      alerts.push({
        title: 'Out of Stock',
        count: stats.out_of_stock_products,
        description: 'Products need restocking',
        icon: ArchiveBoxXMarkIcon,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      });
    }
    
    if (stats.pending_orders > 0) {
      alerts.push({
        title: 'Pending Orders',
        count: stats.pending_orders,
        description: 'Orders awaiting processing',
        icon: ExclamationTriangleIcon,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      });
    }
    
    return alerts;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'confirmed': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'refunded': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">Unable to load dashboard data.</p>
        </div>
      </div>
    );
  }

  const statsCards = getStatsCards();
  const alertCards = getAlertCards();

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 -mx-6 -mt-6 px-6 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600 flex items-center">
              <CalendarDaysIcon className="h-4 w-4 mr-2" />
              Welcome back! Here's your store overview for today.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Last updated</p>
            <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Alert Cards */}
      {alertCards.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {alertCards.map((alert, index) => (
            <div key={index} className={`${alert.bgColor} ${alert.borderColor} border-l-4 p-4 rounded-r-lg`}>
              <div className="flex items-center">
                <alert.icon className={`h-5 w-5 ${alert.color} mr-3`} />
                <div className="flex-1">
                  <h3 className={`text-sm font-semibold ${alert.color}`}>{alert.title}</h3>
                  <div className="flex items-center mt-1">
                    <span className={`text-2xl font-bold ${alert.color} mr-2`}>{alert.count}</span>
                    <span className="text-xs text-gray-600">{alert.description}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => (
          <div key={stat.name} className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-all duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className={`${stat.bgColor} p-3 rounded-xl`}>
                  <stat.icon className={`h-6 w-6 ${stat.textColor}`} aria-hidden="true" />
                </div>
                <div className={`bg-gradient-to-r ${stat.color} w-16 h-1 rounded-full`}></div>
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-1">{stat.name}</h3>
                <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-xs text-gray-600">{stat.subValue}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Orders and Top Products */}
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        {/* Recent Orders */}
        <div className="xl:col-span-2 bg-white shadow-lg rounded-xl border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ShoppingCartIcon className="h-5 w-5 mr-2 text-gray-600" />
                Recent Orders
              </h3>
              <span className="text-sm text-gray-500">{dashboardData.recent_orders.length} orders</span>
            </div>
          </div>
          <div className="overflow-hidden">
            {dashboardData.recent_orders.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {dashboardData.recent_orders.slice(0, 8).map((order: RecentOrder) => (
                  <div key={order.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">
                            #{order.order_number || `ORD-${order.id.toString().padStart(3, '0')}`}
                          </h4>
                          <span className="text-sm text-gray-500">{formatDate(order.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{order.user?.name || 'Unknown Customer'}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(order.total_amount)}
                          </span>
                          <div className="flex space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPaymentStatusColor(order.payment_status)}`}>
                              {order.payment_status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <ShoppingCartIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No recent orders found</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-gray-600" />
                Top Products
              </h3>
              <span className="text-sm text-gray-500">{dashboardData.top_products.length} products</span>
            </div>
          </div>
          <div className="overflow-hidden">
            {dashboardData.top_products.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {dashboardData.top_products.slice(0, 5).map((product: TopProduct, index) => (
                  <div key={product.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white mr-3 ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-600' : 'bg-gray-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">{product.name}</h4>
                          <div className="flex items-center">
                            <span className="text-xs text-gray-500 mr-2">Sales:</span>
                            <span className="text-sm font-medium text-gray-900">{product.total_sold} units</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <ArrowTrendingUpIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No top products data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
